// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ToggleFiltersOpen";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Pluralize from "@monsma/XDL/Components/Common/Pluralize";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    toggleFiltersOpen,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    toggleFilterGridItem?: GridItemProps;
    /** @deprecated Use toggleFilterButtonIcon instead. */
    toggleFilterIcon?: IconPresentationProps;
    /** @deprecated Use toggleFilterButton instead. */
    toggleFilterClickable?: ClickablePresentationProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    headingText?: TypographyPresentationProps;
    countGridItem?: GridItemProps;
}

export const savedOrderListHeaderStyles: SavedOrderListHeaderStyles = {
    container: {
        gap: 8,
        css: css`
            padding-bottom: 20px;
        `,
    },
    titleGridItem: {
        width: 11,
        style: { fontWeight: 600 },
    },
    toggleFilterGridItem: {
        width: 1,
        style: {
            justifyContent: "flex-end",
            alignSelf: "flex-end",
        },
    },
    toggleFilterButton: {
        color: "common.background",
        shape: "pill",
        buttonType: "solid",
        shadow: false,
        css: css`
            width: 40px;
            padding: 0;
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
};
const styles = savedOrderListHeaderStyles;

const SavedOrderListHeader = ({ toggleFiltersOpen, id }: Props) => {
    const savedOrdersDataView = React.useContext(CartsDataViewContext);
    const savedOrdersCount = savedOrdersDataView.value
        ? savedOrdersDataView.pagination
            ? savedOrdersDataView.pagination.totalItemCount
            : 0
        : undefined;
    // xng-change: start: Add the Pluralize component, the Filter label.
    return (
        <>
            <Zone contentId={id} zoneName="Content" />
            <GridContainer {...styles.container}>
                {!!savedOrdersCount && (
                    <GridItem {...styles.countGridItem}>
                        <Pluralize
                            total={savedOrdersCount}
                            pluralLabel={translate("Orders")}
                            singularLabel={translate("Order")}
                        />
                    </GridItem>
                )}
                <GridItem {...styles.toggleFilterGridItem}>
                    <Button
                        {...styles.toggleFilterButton}
                        onClick={() => toggleFiltersOpen()}
                        aria-label={translate("Toggle Filter")}
                        data-test-selector="savedOrderList_showHideFilters"
                    >
                        <ButtonIcon {...styles.toggleFilterButtonIcon} />
                        <Typography {...styles.headingText}>{translate("Filter")}</Typography>
                    </Button>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedOrderListHeader),
    definition: {
        group: "Saved Order List",
        displayName: "Header",
        allowedContexts: ["SavedOrderListPage"],
    },
};

export default widgetModule;
